<script setup>
import { computed, useSlots, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from '../../composables/useAdmin';
import ConfirmationModal from '../../components/widgets/modal/ConfirmationModal.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'goToContactsList',
  'toggleBlock',
  'toggleDomainBlock',
  'mergeContact',
]);

const { t } = useI18n();
const store = useStore();
const slots = useSlots();
const route = useRoute();
const blockContactConfirmationModal = ref(null);
const domainBlockConfirmationModal = ref(null);
const publicDomainConfirmationModal = ref(null);
const showActionsDropdown = ref(false);
// Below lg the contact sidebar is an overlay, opened from the header
const isMobileSidebarOpen = ref(false);

const contactId = computed(() => route.params.contactId);
const phoneStatus = useMapGetter('phone/getStatus');
const currentAccount = useMapGetter('accounts/getCurrentAccount');

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const isContactDomainBlocked = computed(() => {
  return props.selectedContact?.domainBlocked;
});

const isContactDomainWhitelisted = computed(() => {
  return props.selectedContact?.domainWhitelisted;
});

const contactEmail = computed(() => props.selectedContact?.email || '');

const contactDomain = computed(() => contactEmail.value.split('@').pop());

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = async () => {
  const result = await blockContactConfirmationModal.value.showConfirmation();
  if (!result) return;

  emit('toggleBlock', isContactBlocked.value);
  showActionsDropdown.value = false;
};

const toggleDomainBlock = async () => {
  // A public (freemail) domain is shared by unrelated senders, so it can never
  // be blocked as a whole. Offer to block this single contact instead.
  if (!isContactDomainBlocked.value && isContactDomainWhitelisted.value) {
    const blockContact =
      await publicDomainConfirmationModal.value.showConfirmation();
    // `false` = "not blocked yet", so the handler blocks instead of toggling —
    // confirming here must never unblock an already blocked contact.
    if (blockContact) emit('toggleBlock', false);
    showActionsDropdown.value = false;
    return;
  }

  const result = await domainBlockConfirmationModal.value.showConfirmation();
  if (!result) return;

  emit('toggleDomainBlock', isContactDomainBlocked.value);
  showActionsDropdown.value = false;
};

const dialContact = () => {
  const phoneNumber = props.selectedContact?.phoneNumber;
  if (currentAccount.value.dialing_mode === 'direct') {
    store.dispatch('phone/makeCall', {
      number: phoneNumber,
    });
  } else {
    emitter.emit(BUS_EVENTS.SHOW_PHONE_DIALER, phoneNumber);
  }
};

const { isAdmin: isAdminUser } = useAdmin();

const contactMenuItems = computed(() => {
  const items = [
    {
      label: isContactBlocked.value
        ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
        : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT'),
      action: 'toggleBlock',
      value: 'toggleBlock',
      icon: isContactBlocked.value ? 'i-lucide-check-circle' : 'i-lucide-ban',
      disabled: isContactDomainBlocked.value,
    },
  ];

  if (isAdminUser.value) {
    items.push({
      label: isContactDomainBlocked.value
        ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT_DOMAIN')
        : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT_DOMAIN'),
      action: 'toggleDomainBlock',
      value: 'toggleDomainBlock',
      icon: isContactDomainBlocked.value
        ? 'i-lucide-check-circle'
        : 'i-lucide-shield-ban',
    });
  }

  return items;
});

const handleContactAction = ({ action }) => {
  if (action === 'toggleBlock') {
    toggleBlock();
  } else if (action === 'toggleDomainBlock') {
    toggleDomainBlock();
  }
};
</script>

<template>
  <section
    class="flex w-full h-full overflow-hidden justify-evenly bg-n-background"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
    >
      <header class="sticky top-0 z-10 px-6 3xl:px-0">
        <div class="w-full mx-auto max-w-[40.625rem]">
          <div
            class="flex flex-wrap items-center justify-between w-full min-h-20 py-4 gap-2"
          >
            <Breadcrumb
              :items="breadcrumbItems"
              @click="handleBreadcrumbClick"
            />
            <div class="flex flex-wrap items-center gap-2">
              <Button
                :label="t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                size="sm"
                @click="
                  emitter.emit(BUS_EVENTS.OPEN_NEW_CONVERSATION, { contactId })
                "
              />
              <Button
                v-if="
                  phoneStatus === 'connected' &&
                  props.selectedContact?.phoneNumber !== null
                "
                :label="t('CONTACTS_LAYOUT.HEADER.DIAL')"
                size="sm"
                @click="dialContact"
              />
              <Button
                :label="t('CONTACT_PANEL.MERGE_CONTACT')"
                icon="i-lucide-merge"
                color="slate"
                size="sm"
                @click="emit('mergeContact')"
              />

              <div
                v-on-clickaway="() => (showActionsDropdown = false)"
                class="relative"
              >
                <Button
                  :label="t('CONTACTS_LAYOUT.HEADER.BLOCKING')"
                  icon="i-lucide-chevron-down"
                  color="slate"
                  size="sm"
                  :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
                  @click="showActionsDropdown = !showActionsDropdown"
                />
                <DropdownMenu
                  v-if="showActionsDropdown"
                  :menu-items="contactMenuItems"
                  class="ltr:right-0 rtl:left-0 mt-1 w-64 top-full"
                  @action="handleContactAction($event)"
                />
              </div>
              <Button
                v-if="slots.sidebar"
                icon="i-lucide-panel-right-open"
                color="slate"
                size="sm"
                class="lg:hidden rtl:rotate-180"
                :aria-label="t('CONTACTS_LAYOUT.HEADER.SHOW_DETAILS')"
                @click="isMobileSidebarOpen = true"
              />
            </div>
          </div>
        </div>
      </header>
      <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
        <div class="w-full py-4 mx-auto max-w-[40.625rem]">
          <slot name="default" />
        </div>
      </main>
    </div>

    <div
      v-if="slots.sidebar"
      class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-3 max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <slot name="sidebar" />
    </div>
    <template v-if="slots.sidebar && isMobileSidebarOpen">
      <div
        class="fixed inset-0 z-30 bg-modal-backdrop-light dark:bg-modal-backdrop-dark lg:hidden"
        @click="isMobileSidebarOpen = false"
      />
      <div
        class="fixed top-0 ltr:right-0 rtl:left-0 z-40 h-full w-[85%] max-w-md overflow-y-auto pb-3 ltr:border-l rtl:border-r border-n-weak bg-n-solid-2 shadow-lg lg:hidden"
      >
        <div class="flex justify-end px-2 pt-2">
          <Button
            icon="i-lucide-x"
            slate
            ghost
            sm
            :aria-label="t('GENERAL.CLOSE')"
            @click="isMobileSidebarOpen = false"
          />
        </div>
        <slot name="sidebar" />
      </div>
    </template>
    <ConfirmationModal
      ref="blockContactConfirmationModal"
      :title="
        isContactBlocked
          ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
          : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
      "
      :description="
        isContactBlocked
          ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT_MESSAGE')
          : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT_MESSAGE')
      "
      :confirm-label="t('CONTACT_PANEL.CONFIRM.YES')"
      :cancel-label="t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
    <ConfirmationModal
      ref="domainBlockConfirmationModal"
      :title="
        isContactDomainBlocked
          ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT_DOMAIN')
          : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT_DOMAIN')
      "
      :description="
        isContactDomainBlocked
          ? t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT_DOMAIN_MESSAGE')
          : t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT_DOMAIN_MESSAGE')
      "
      :confirm-label="t('CONTACT_PANEL.CONFIRM.YES')"
      :cancel-label="t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
    <ConfirmationModal
      ref="publicDomainConfirmationModal"
      :title="t('CONTACTS_LAYOUT.HEADER.BLOCK_PUBLIC_DOMAIN_TITLE')"
      :description="
        t('CONTACTS_LAYOUT.HEADER.BLOCK_PUBLIC_DOMAIN_MESSAGE', {
          domain: contactDomain,
          email: contactEmail,
        })
      "
      :confirm-label="t('CONTACTS_LAYOUT.HEADER.BLOCK_ONLY_THIS_CONTACT')"
      :cancel-label="t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
  </section>
</template>
